/**
 * 站点页头 —— 首页与内容页共用的唯一 header(视觉语言 wv-h2-*)。
 *
 * 两种形态:
 * - overlay:首页专用。浮在 hero 之上透明,滚过 hero 后变白底(is-scrolled)。
 * - solid:内容/表单页。没有 hero 可浮,白底 sticky。
 *
 * 链接集合由调用方给:首页传页内锚点(#about,平滑滚动),其它页传跨页
 * 链接(/#about,整页导航 —— spec §4:跨 app/跨页一律真实 <a href>)。
 * 登录/注册这类转化漏斗页不传 links,只留品牌 + 会话区。
 */
import { useEffect, useState } from 'react';
import { appUrl } from '@web-v4/router-utils';
import { smoothScrollToHash } from '../lib/smooth-scroll';
import { SiteSession } from './SiteSession';

export interface NavLink {
  readonly href: string;
  readonly label: string;
  readonly external?: boolean;
}

/** 首页版:页内锚点,平滑滚动。 */
export const HOME_NAV_LINKS: readonly NavLink[] = [
  { href: '#about', label: '关于' },
  { href: '#gallery', label: '画廊' },
  { href: '#donate', label: '捐助' },
  { href: '#help', label: '帮助' },
  { href: '/acidisland', label: '海岛服' },
  { href: 'https://map.evermc.com/', label: '服务器地图', external: true },
];

/** 内容页版:同一组目的地,写成回首页的跨页链接。 */
export const SITE_NAV_LINKS: readonly NavLink[] = HOME_NAV_LINKS.map((link) =>
  link.href.startsWith('#') ? { ...link, href: `/${link.href}` } : link,
);

export interface SiteHeaderProps {
  readonly variant: 'overlay' | 'solid';
  /** 省略即只渲染品牌 + 会话区(登录/注册等漏斗页)。 */
  readonly links?: readonly NavLink[];
}

export function SiteHeader({ variant, links }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isOverlay = variant === 'overlay';

  useEffect(() => {
    if (!isOverlay) {
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isOverlay]);

  const navClass = [
    'wv-h2-nav',
    isOverlay ? 'wv-h2-nav--overlay' : 'wv-h2-nav--solid',
    isOverlay && scrolled ? 'is-scrolled' : '',
    open ? 'is-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // 首页上品牌回到页顶(平滑),其它页是回首页的真实导航。
  const brandHref = isOverlay ? '#top' : appUrl('portal');

  return (
    <nav className={navClass} aria-label="站点导航">
      <a
        className="wv-h2-nav-brand"
        href={brandHref}
        onClick={(e) => {
          setOpen(false);
          if (isOverlay) smoothScrollToHash(e, '#top');
        }}
      >
        {/* Dual-state brand: white-text logo over the dark hero, dark-text
            logo once the nav bar turns white (solid/is-scrolled/is-open). */}
        <img
          className="wv-h2-nav-brand__img wv-h2-nav-brand__img--light"
          src="/static/images/logo-user.png"
          alt="随便MC"
        />
        <img
          className="wv-h2-nav-brand__img wv-h2-nav-brand__img--dark"
          src="/static/images/logo-re-3-small.png"
          alt=""
          aria-hidden="true"
        />
      </a>
      {links !== undefined && (
        <div className="wv-h2-nav-links">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              {...(l.external === true ? { target: '_blank', rel: 'noreferrer' } : {})}
              onClick={(e) => {
                setOpen(false);
                if (l.href.startsWith('#')) smoothScrollToHash(e, l.href);
              }}
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
      <SiteSession onNavigate={() => setOpen(false)} />
      {links !== undefined && (
        <button
          type="button"
          className="wv-h2-burger"
          aria-label={open ? '关闭导航菜单' : '打开导航菜单'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}
    </nav>
  );
}
